/**
 * NotificationsMixin - Централизованные уведомления
 * 
 * Предоставляет унифицированные методы для работы с уведомлениями:
 * - SweetAlert модальные окна
 * - Toast уведомления (группируемые как в ВК)
 * - Подтверждения действий
 * 
 * Использует стили из notifications-common.scss (подключен глобально)
 */

import ToastNotification from '@/components/ToastNotification.vue';
import notificationSound from '@/utils/notificationSound';

export default {
  methods: {
    /**
     * Закрыть все уведомления
     */
    $clearAllNotifications() {
      this.$toast.clear();
    },

    /**
     * Показать стекирующееся уведомление (как в ВК)
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} icon - Material Design Icon (например: 'mdi-bell', 'mdi-check-circle')
     * @param {string} type - Тип: 'success', 'error', 'warning', 'info', 'default'
     * @param {Function} onClick - Callback при клике
     */
    $notify({ title, message, icon = 'mdi-bell', type = 'default', onClick = null }) {
      // 🔔 Воспроизводим звук уведомления
      notificationSound.play();
      
      this.$toast(
        {
          component: ToastNotification,
          props: {
            title,
            message,
            icon
          }
        },
        {
          type,
          onClick
        }
      );
    },

    /**
     * 1. Уведомление о новой задаче
     * Backend: Task::sendNotification()
     */
    $notifyTask(taskName, taskTime, taskId) {
      this.$notify({
        title: '📅 Напоминание о задаче',
        message: `📌 ${taskName}\n\n🕐 Время: ${taskTime}`,
        icon: 'mdi-calendar-check',
        type: 'info',
        onClick: () => {
          this.$router.push(`/tasks?task_id=${taskId}`);
        }
      });
    },

    /**
     * 2. Уведомление о задачах на день
     * Backend: Task::notifyForDay()
     */
    $notifyDailyTasks(count, date) {
      this.$notify({
        title: '📅 Задачи на сегодня',
        message: `📋 У вас ${count} ${count === 1 ? 'задача' : count < 5 ? 'задачи' : 'задач'} на ${date}\n\n🎯 Не забудьте выполнить!`,
        icon: 'mdi-calendar-multiple-check',
        type: 'info',
        onClick: () => {
          this.$router.push('/tasks');
        }
      });
    },

    /**
     * Напоминание о задаче (Reminder)
     * Backend: SendTaskReminders
     * Получатели: Исполнители задачи с task_notification=true
     */
    $notifyTaskReminder(taskName, taskTime, timeUntil, taskId) {
      this.$notify({
        title: '⏰ Напоминание о задаче',
        message: `📋 ${taskName}\n📅 ${taskTime}\n⏳ Осталось: ${timeUntil}`,
        icon: 'mdi-bell-ring',
        type: 'info',
        onClick: () => {
          this.$router.push(`/task/${taskId}`);
        }
      });
    },

    /**
     * 3. Уведомление о регулярном платеже
     * Backend: RegularPaymentNotification
     * Получатели: ТОЛЬКО администраторы (access_level >= 2)
     */
    $notifyRegularPayment(recipient, amount, paymentId) {
      this.$notify({
        title: '💰 Регулярный платеж',
        message: `💳 Получатель: ${recipient}${amount ? `\n💵 Сумма: ${amount} ₽` : ''}\n\n📅 Требуется внимание`,
        icon: 'mdi-cash-clock',
        type: 'warning',
        onClick: () => {
          this.$router.push(`/money?payment_id=${paymentId}`);
        }
      });
    },

    /**
     * 4. Уведомление об обновлении подписки
     * Backend: NotifySubscriptionUpdated
     * Получатели: Администраторы (access_level >= 2)
     */
    $notifySubscriptionUpdated(planName, userLimit, modules, expiresAt) {
      this.$notify({
        title: '✨ Подписка обновлена',
        message: `${planName ? `📦 ${planName}\n` : ''}👥 Лимит пользователей: ${userLimit || 'без ограничений'}${modules ? `\n🔧 Модули: ${modules}` : ''}\n\n📅 Истекает: ${expiresAt}`,
        icon: 'mdi-crown',
        type: 'success'
      });
    },

    /**
     * 4.1. Уведомление об истечении подписки (< 3 дней)
     * Backend: NotifySubscriptionExpiring (команда subscription:notify-expiring)
     * Получатели: Администраторы (access_level >= 2)
     * Каналы: SMTP, Push, Telegram, Toast
     */
    $notifySubscriptionExpiring(daysRemaining, expiresAt, subscriptionId) {
      const daysWord = daysRemaining === 1 ? 'день' : (daysRemaining < 5 ? 'дня' : 'дней');
      this.$notify({
        title: '⚠️ Подписка истекает',
        message: `Осталось ${daysRemaining} ${daysWord}. Истекает: ${expiresAt || '—'}. Продлите подписку в разделе «Подписка».`,
        icon: 'mdi-alert-circle',
        type: 'warning',
        onClick: () => {
          this.$router.push('/subscription');
        }
      });
    },

    /**
     * 5. Уведомление о приглашении в компанию
     * Backend: CustomerController
     */
    $notifyCompanyInvitation(companyName, companyId) {
      this.$notify({
        title: '👥 Приглашение в компанию',
        message: `🎉 Вас пригласили в компанию\n🏢 ${companyName}\n\n✅ Теперь у вас есть доступ`,
        icon: 'mdi-account-multiple-plus',
        type: 'info',
        onClick: () => {
          this.$router.push(`/customers?company_id=${companyId}`);
        }
      });
    },

    /**
     * 6. Уведомление о новой заявке (Платный модуль)
     * Backend: OrderRequest
     * Получатели: Сотрудники с order_notification=true
     */
    $notifyNewOrderRequest(customerName, ticketKey, requestId) {
      this.$notify({
        title: '📄 Новая заявка',
        message: `📥 Получена новая заявка на заказ\n\n👤 От: ${customerName}\n🎫 Номер: ${ticketKey}`,
        icon: 'mdi-file-document-plus',
        type: 'success',
        onClick: () => {
          this.$router.push(`/requests?request_id=${requestId}`);
        }
      });
    },

    /**
     * 7. Уведомление о новом email в заявке (Платный модуль)
     * Backend: ImapService
     * Получатели: Сотрудники с order_notification=true
     */
    $notifyNewEmailInRequest(ticketKey, from, requestId) {
      this.$notify({
        title: '📧 Новое сообщение в заявке',
        message: `📩 Получено новое письмо\n\n🎫 Заявка: #${ticketKey}${from ? `\n👤 От: ${from}` : ''}`,
        icon: 'mdi-email-open',
        type: 'info',
        onClick: () => {
          this.$router.push(`/requests?request_id=${requestId}`);
        }
      });
    },

    /**
     * Уведомление о новом платеже по вебхуку Точка Банк
     * Backend: TochkaWebhookController (после сохранения платежа)
     * Получатели: сотрудники с order_notification=true (как «Новая заявка»)
     * Каналы: Telegram (если включён модуль и у пользователя tg_channel_id), Push, браузер (toast)
     * @param {string} amount - отформатированная сумма
     * @param {boolean} isIncome - поступление или списание
     * @param {string} payer - плательщик/получатель (для СБП — реальное имя из purpose)
     * @param {number|null} paymentId - id платежа для перехода
     * @param {string} shortPurpose - краткое назначение платежа (опционально)
     */
    $notifyTochkaPayment(amount, isIncome, payer, paymentId, shortPurpose = '') {
      const direction = isIncome ? 'Поступление' : 'Списание';
      const shortPayer = payer && payer.length > 35 ? payer.slice(0, 32) + '…' : (payer || '—');
      let message = `${amount} ₽${shortPayer !== '—' ? '\n👤 ' + shortPayer : ''}`;
      if (shortPurpose && message.length + shortPurpose.length < 150) {
        message += '\n' + shortPurpose;
      }
      this.$notify({
        title: '💳 Точка Банк: ' + direction,
        message,
        icon: 'mdi-bank-transfer',
        type: isIncome ? 'success' : 'info',
        onClick: () => {
          this.$router.push(paymentId ? `/money?payment_id=${paymentId}` : '/money');
        }
      });
    },

    /**
     * 8. Уведомление о входящем звонке (Novofon)
     * Backend: NovofonController
     * Получатели: ВСЕ сотрудники компании
     * Срабатывает СРАЗУ при входящем звонке
     */
    $notifyMissedCall(customerName, phone, customerId, duration, isSpam = false) {
      const phoneLine = phone ? `\n📱 ${phone}` : '';
      const spamLine = isSpam ? '\n🚫 СПАМ' : '';
      this.$notify({
        title: isSpam ? 'СПАМ · Входящий звонок' : '📞 Входящий звонок',
        message: `☎️ Звонит:\n\n👤 ${customerName}${phoneLine}${spamLine}${duration ? `\n⏱️ Длительность: ${duration}` : ''}`,
        icon: isSpam ? 'mdi-alert-octagon' : 'mdi-phone-incoming',
        type: isSpam ? 'error' : 'warning',
        onClick: () => {
          if (customerId) {
            this.$router.push(`/customers/${customerId}`);
            return;
          }
          this.$router.push('/novofon');
        }
      });
    },

    /**
     * Пропущенный звонок (после завершения, Novofon Data API)
     */
    $notifyCallMissed(customerName, phone, route = null) {
      this.$notify({
        title: '📞 Пропущенный звонок',
        message: `👤 ${customerName}\n📱 ${phone}`,
        icon: 'mdi-phone-missed',
        type: 'error',
        onClick: () => {
          this.$router.push(route && route.startsWith('/') ? route : '/novofon');
        }
      });
    },

    /**
     * 9. Уведомление о создании нового заказа с безналом
     * Backend: Order::sendNotificationToAdmin()
     * Получатели: ТОЛЬКО администраторы (access_level >= 2)
     * Условие: account->cashless = true
     */
    $notifyNewOrder(orderNumber, customerName, orderId) {
      this.$notify({
        title: '📋 Создан заказ с безналом',
        message: `✅ Заказ №${orderNumber}${customerName ? `\n👤 Клиент: ${customerName}` : ''}\n\n💳 Безналичная оплата\n🎯 Требуется выставить счет`,
        icon: 'mdi-file-plus',
        type: 'success',
        onClick: () => {
          if (orderId) {
            this.$router.push(`/orders/${orderId}`);
          } else {
            this.$router.push('/orders');
          }
        }
      });
    },

    /**
     * 10. Уведомление о постановке в очередь на печать
     * Backend: Order::sendNotificationToResponsible()
     * Получатели: ТОЛЬКО ответственный за наряд-заказ (responsible_user_id)
     * Условие: status = 'queue'
     * 
     * ⚠️ ВАЖНО: Это ЕДИНСТВЕННОЕ уведомление об изменении статуса заказа,
     * которое отправляется в данный момент. Для других статусов уведомления не настроены.
     */
    $notifyOrderInPrintQueue(orderNumber, orderId) {
      this.$notify({
        title: '🖨️ Заказ в очереди на печать',
        message: `📄 Заказ №${orderNumber}\n✅ Поставлен в очередь на печать\n\n🎯 Можно начинать работу`,
        icon: 'mdi-printer',
        type: 'success',
        onClick: () => {
          if (orderId) {
            this.$router.push(`/orders/${orderId}`);
          } else {
            this.$router.push('/orders');
          }
        }
      });
    },

    /**
     * 11. [РЕЗЕРВ] Уведомление об изменении статуса заказа (для будущего использования)
     * Backend: НЕ РЕАЛИЗОВАНО - уведомления отправляются только для статуса 'queue' (см. выше)
     * 
     * Этот метод создан для будущего расширения функционала,
     * когда понадобится отправлять уведомления при изменении других статусов.
     */
    $notifyOrderStatusChanged(orderNumber, newStatus, orderId) {
      this.$notify({
        title: '🔄 Статус заказа изменён',
        message: `📦 Заказ №${orderNumber}\n📊 Новый статус: ${newStatus}\n\n👁️ Проверьте детали`,
        icon: 'mdi-swap-horizontal',
        type: 'info',
        onClick: () => {
          if (orderId) {
            this.$router.push(`/orders/${orderId}`);
          } else {
            this.$router.push('/orders');
          }
        }
      });
    },

    /**
     * 12. Уведомление о новой новости в Telegram канале
     * Backend: Laravel Echo (NewTelegramPost event)
     * Когда: Новый пост добавлен в Telegram канал CRM МояФабрика
     */
    $notifyTelegramNews(text, isPinned = false) {
      const shortText = text ? (text.length > 120 ? text.substring(0, 120) + '...' : text) : 'Новое сообщение в канале';
      this.$notify({
        title: '📰 Новая новость CRM МояФабрика',
        message: `${isPinned ? '📌 Закрепленное сообщение\n\n' : ''}${shortText}\n\n👉 Перейти к новостям`,
        icon: 'mdi-newspaper-variant',
        type: 'info',
        onClick: () => {
          this.$router.push('/support');
        }
      });
    },

    /**
     * 13. Воронка продаж: Просроченная задача по лиду
     * Backend: FunnelNotificationService::generateOverdueTaskNotifications()
     */
    $notifyOverdueTask(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `⏰ ${title}`,
        message: message,
        icon: 'mdi-alert-circle',
        type: 'warning',
        onClick: () => {
          if (leadableType && leadableId) {
            // Определяем роут в зависимости от типа лида
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 14. Воронка продаж: Лид без ответственного
     * Backend: FunnelNotificationService::generateUnassignedLeadNotifications()
     */
    $notifyUnassignedLead(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `👤 ${title}`,
        message: message,
        icon: 'mdi-account-alert',
        type: 'info',
        onClick: () => {
          if (leadableType && leadableId) {
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 15. Воронка продаж: Высокоприоритетный лид без активности
     * Backend: FunnelNotificationService::generateHighPriorityNoActivityNotifications()
     */
    $notifyHighPriorityNoActivity(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `🔥 ${title}`,
        message: message,
        icon: 'mdi-fire-alert',
        type: 'error',
        onClick: () => {
          if (leadableType && leadableId) {
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 16. Воронка продаж: Достижение целевого показателя конверсии
     * Backend: FunnelNotificationService::generateConversionMilestoneNotifications()
     */
    $notifyConversionMilestone(title, message, notificationId) {
      this.$notify({
        title: title,
        message: message,
        icon: 'mdi-trophy',
        type: 'success',
        onClick: () => {
          this.$router.push('/funnel');
        }
      });
    },

    /**
     * 17. Воронка продаж: Напоминание - клиент не звонил более 14 дней
     * Backend: FunnelNotificationService::generateNoCallReminders()
     */
    $notifyNoCallReminder(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `📞 ${title}`,
        message: message,
        icon: 'mdi-phone-off',
        type: 'warning',
        onClick: () => {
          if (leadableType && leadableId && leadableType.includes('Customer')) {
            this.$router.push(`/customers/${leadableId}`);
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 19. Воронка продаж: Новая задача по клиенту
     * Backend: FunnelNotificationService::notifyCustomerTaskCreated()
     */
    $notifyCustomerTaskCreated(title, message, notificationId, leadableType, leadableId, taskId) {
      this.$notify({
        title: `✅ ${title}`,
        message: message,
        icon: 'mdi-check-circle',
        type: 'info',
        onClick: () => {
          if (leadableType && leadableId && leadableType.includes('Customer')) {
            this.$router.push(`/customers/${leadableId}`);
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 20. Воронка продаж: Переход на новый этап воронки
     * Backend: FunnelNotificationService::notifyStageProgression()
     */
    $notifyStageProgression(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `📈 ${title}`,
        message: message,
        icon: 'mdi-trending-up',
        type: 'success',
        onClick: () => {
          if (leadableType && leadableId) {
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 21. Воронка продаж: Назначение/снятие менеджера с лида
     * Backend: FunnelNotificationService::notifyLeadAssigned()
     */
    $notifyLeadAssigned(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `👤 ${title}`,
        message: message,
        icon: 'mdi-account-switch',
        type: 'info',
        onClick: () => {
          if (leadableType && leadableId) {
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },

    /**
     * 22. Воронка продаж: Высокий приоритет лида (lead_score >= 80)
     * Backend: FunnelNotificationService::notifyHighLeadScore()
     */
    $notifyHighLeadScore(title, message, notificationId, leadableType, leadableId) {
      this.$notify({
        title: `🎯 ${title}`,
        message: message,
        icon: 'mdi-star',
        type: 'warning',
        onClick: () => {
          if (leadableType && leadableId) {
            if (leadableType.includes('Customer')) {
              this.$router.push(`/customers/${leadableId}`);
            } else if (leadableType.includes('OrderRequest')) {
              this.$router.push(`/requests?request_id=${leadableId}`);
            } else {
              this.$router.push(`/funnel/lead/${leadableType.split('\\').pop().toLowerCase()}/${leadableId}`);
            }
          } else {
            this.$router.push('/funnel');
          }
        }
      });
    },
    /**
     * Получить основной цвет из настроек CRM
     */
    getPrimaryColor() {
      return this.$store?.getters?.company?.settings?.primary_color || 
             getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || 
             '#1976d2';
    },
    
    /**
     * Показать подтверждающее диалоговое окно
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки подтверждения (по умолчанию: "Да")
     * @param {string} cancelText - Текст кнопки отмены (по умолчанию: "Отмена")
     * @returns {Promise<boolean>} - true если пользователь подтвердил, false если отменил
     */
    async $confirm(title = 'Подтверждение', message = 'Вы уверены?', confirmText = 'Да', cancelText = 'Отмена') {
      const primaryColor = this.getPrimaryColor();
      const result = await this.$swal.fire({
        title,
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        confirmButtonColor: primaryColor,
        cancelButtonColor: '#9e9e9e',
        customClass: {
          popup: 'swal-confirm',
          confirmButton: 'swal-btn-confirm',
          cancelButton: 'swal-btn-cancel'
        },
        buttonsStyling: false
      });
      return result.isConfirmed;
    },

    /**
     * Показать предупреждающее диалоговое окно
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки подтверждения (по умолчанию: "Продолжить")
     * @param {string} cancelText - Текст кнопки отмены (по умолчанию: "Отмена")
     * @returns {Promise<boolean>} - true если пользователь подтвердил, false если отменил
     */
    async $confirmWarning(title = 'Внимание!', message = 'Это действие может иметь последствия', confirmText = 'Продолжить', cancelText = 'Отмена') {
      const result = await this.$swal.fire({
        title,
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        confirmButtonColor: '#ff9800',
        cancelButtonColor: '#9e9e9e',
        customClass: {
          popup: 'swal-warning',
          confirmButton: 'swal-btn-warning',
          cancelButton: 'swal-btn-cancel'
        },
        buttonsStyling: false
      });
      return result.isConfirmed;
    },

    /**
     * Показать опасное подтверждающее диалоговое окно (для удаления и т.п.)
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки подтверждения (по умолчанию: "Удалить")
     * @param {string} cancelText - Текст кнопки отмены (по умолчанию: "Отмена")
     * @returns {Promise<boolean>} - true если пользователь подтвердил, false если отменил
     */
    async $confirmDanger(title = 'Удалить?', message = 'Это действие нельзя отменить', confirmText = 'Удалить', cancelText = 'Отмена') {
      const result = await this.$swal.fire({
        title,
        text: message,
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        confirmButtonColor: '#f44336',
        cancelButtonColor: '#9e9e9e',
        customClass: {
          popup: 'swal-danger',
          confirmButton: 'swal-btn-danger',
          cancelButton: 'swal-btn-cancel'
        },
        buttonsStyling: false
      });
      return result.isConfirmed;
    },

    /**
     * Показать toast-уведомление об успехе
     * @param {string} message - Текст сообщения
     * @param {number} duration - Длительность в мс (по умолчанию: 3000)
     */
    $toastSuccess(message, duration = 3000) {
      this.$swal.fire({
        icon: 'success',
        title: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: duration,
        timerProgressBar: true,
        customClass: {
          popup: 'toast-success'
        }
      });
    },

    /**
     * Показать toast-уведомление об ошибке
     * @param {string} message - Текст сообщения
     * @param {number} duration - Длительность в мс (по умолчанию: 4000)
     */
    $toastError(message, duration = 4000) {
      this.$swal.fire({
        icon: 'error',
        title: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: duration,
        timerProgressBar: true,
        customClass: {
          popup: 'toast-error'
        }
      });
    },

    /**
     * Показать toast-уведомление с предупреждением
     * @param {string} message - Текст сообщения
     * @param {number} duration - Длительность в мс (по умолчанию: 3500)
     */
    $toastWarning(message, duration = 3500) {
      this.$swal.fire({
        icon: 'warning',
        title: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: duration,
        timerProgressBar: true,
        customClass: {
          popup: 'toast-warning'
        }
      });
    },

    /**
     * Показать toast-уведомление с информацией
     * @param {string} message - Текст сообщения
     * @param {number} duration - Длительность в мс (по умолчанию: 3000)
     */
    $toastInfo(message, duration = 3000) {
      this.$swal.fire({
        icon: 'info',
        title: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: duration,
        timerProgressBar: true,
        customClass: {
          popup: 'toast-info'
        }
      });
    },

    /**
     * Показать модальное окно с информацией
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки (по умолчанию: "OK")
     */
    async $alert(title, message, confirmText = 'OK') {
      const primaryColor = this.getPrimaryColor();
      await this.$swal.fire({
        title,
        text: message,
        icon: 'info',
        confirmButtonText: confirmText,
        confirmButtonColor: primaryColor,
        customClass: {
          popup: 'swal-info',
          confirmButton: 'swal-btn-confirm'
        },
        buttonsStyling: false
      });
    },

    /**
     * Уведомление об упоминании в комментарии
     * Backend: CommentMentionCreated event
     */
    $notifyCommentMention({ authorName, orderNumber, customerName, commentText, orderId }) {
      this.$notify({
        title: '💬 Вас упомянули в комментарии',
        message: `👤 Автор: ${authorName}\n📦 Заказ: #${orderNumber}\n🏢 ${customerName}\n\n💭 ${commentText}`,
        icon: 'mdi-at',
        type: 'info',
        onClick: () => {
          this.$router.push(`/orders/${orderId}`);
        }
      });
    },

    /**
     * Показать модальное окно с успехом
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки (по умолчанию: "OK")
     */
    async $alertSuccess(title, message, confirmText = 'OK') {
      const primaryColor = this.getPrimaryColor();
      await this.$swal.fire({
        title,
        text: message,
        icon: 'success',
        confirmButtonText: confirmText,
        confirmButtonColor: primaryColor,
        customClass: {
          popup: 'swal-success',
          confirmButton: 'swal-btn-success'
        },
        buttonsStyling: false
      });
    },

    /**
     * Показать модальное окно с ошибкой
     * @param {string} title - Заголовок
     * @param {string} message - Текст сообщения
     * @param {string} confirmText - Текст кнопки (по умолчанию: "OK")
     */
    async $alertError(title, message, confirmText = 'OK') {
      await this.$swal.fire({
        title,
        text: message,
        icon: 'error',
        confirmButtonText: confirmText,
        confirmButtonColor: '#f44336',
        customClass: {
          popup: 'swal-error',
          confirmButton: 'swal-btn-danger'
        },
        buttonsStyling: false
      });
    }
  }
};

